import { Upload } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/api';
import type { DossierVnnicFiles } from '@/api/types';
import { Badge } from '@/components/Badge';
import { Callout, Field } from '@/components/blocks';
import { useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { FormActions } from '../compliance/ui';
import { errorCode } from '@/lib/dossierPanel';
import { useEnumLabel } from '../accounts/shared';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

const readBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.readAsDataURL(file);
  });

/**
 * The files VNNIC's HSTM receives for a `.vn` dossier (VNNIC HSTM-API v1.3 Appendix 2 for the
 * case): each required file with its signed status and whether the dossier holds it; staff can
 * upload a file for a slot (checked against VNNIC's file rule; a PDF's signature by
 * identity-worker) and mark a name with a sensitive keyword.
 */
export function HstmPanel({ dossierId, plan, onChanged }: { dossierId: string; plan: DossierVnnicFiles; onChanged: () => void }) {
  const { t } = useTranslation('dossiers');
  const { t: td } = useTranslation('domains');
  const { t: tc } = useTranslation();
  const { t: tp } = useTranslation('domainPage');
  const label = useEnumLabel('accounts');
  const tr = td as unknown as Lookup;
  const fileType = (n: number) => tr(`dossier.fileTypes.${n}`, { defaultValue: `#${n}` });
  const signed = (n: number) => tr(`dossier.signedStatus.${n}`, { defaultValue: `${n}` });
  const [purpose, setPurpose] = useState(plan.slots[0] ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [reason, setReason] = useState('');
  const upload = useWrite(async (_: null, opts) => {
    if (!file) return null;
    const data = await readBase64(file);
    return api.uploadDossierFile(dossierId, { purpose, mime_type: file.type, data, reason: reason.trim() }, opts);
  });
  const keyword = useWrite((b: { sensitive_keyword: boolean; reason: string }, opts) => api.setDossierCase(dossierId, b, opts));
  const method = plan.certify_method !== null ? tr(`dossier.certifyMethods.${plan.certify_method}`, { defaultValue: plan.certify_method_name ?? '' }) : '—';
  return (
    <section className="grid gap-2 border-t pt-4">
      <h3 className="text-sm font-semibold">{td('dossier.vnnicFiles')}</h3>
      <p className="text-xs text-muted-foreground">{td('dossier.vnnicFilesBody', { docType: plan.document_type_name ?? plan.operation, method })}</p>
      <ul className="grid gap-1.5 text-sm">
        {plan.required.map((r) => {
          const types = r.file_type !== null ? [r.file_type] : (r.any_of ?? []).flat();
          const present = plan.files.some((f) => types.includes(f.file_type) && f.signed_status === r.signed_status);
          return (
            <li key={r.key} className="flex flex-wrap items-center gap-2">
              <span>{r.file_type !== null ? fileType(r.file_type) : (r.any_of ?? []).map((alt) => alt.map(fileType).join(' + ')).join(' / ')}</span>
              <Badge tone="neutral" icon={false}>
                {signed(r.signed_status)}
              </Badge>
              {present ? <Badge tone="ok">{td('dossier.hstmSent')}</Badge> : <Badge tone="warn">{td('dossier.missing')}</Badge>}
            </li>
          );
        })}
      </ul>
      {plan.errors.length ? (
        <Callout tone="warn" title={td('dossier.hstmErrors')}>
          <ul className="list-disc pl-4">
            {plan.errors.map((e) => (
              <li key={e} title={e}>{errorCode(e) === 'OTHER' ? e : tp(`dossierPanel.blocker.${errorCode(e)}`)}</li>
            ))}
          </ul>
        </Callout>
      ) : null}
      {plan.source ? <p className="text-xs text-muted-foreground">{plan.source}</p> : null}
      {plan.slots.length ? (
        <form
          className="grid gap-3"
          onSubmit={async (e) => {
            e.preventDefault();
            if (file && reason.trim() && (await upload.run(null))) {
              setFile(null);
              setReason('');
              onChanged();
            }
          }}
        >
          <Field label={t('hstm.slot')} hint={t('hstm.slotHint')}>
            <select className="h-9 rounded-md border bg-background px-2 text-sm" value={purpose} onChange={(e) => setPurpose(e.target.value)} disabled={upload.busy}>
              {plan.slots.map((s) => (
                <option key={s} value={s}>
                  {label('purpose', s)}
                </option>
              ))}
            </select>
          </Field>
          <input type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e) => setFile(e.target.files?.[0] ?? null)} disabled={upload.busy} />
          <Field label={t('hstm.reason')}>
            <Textarea rows={2} maxLength={2000} value={reason} onChange={(e) => setReason(e.target.value)} disabled={upload.busy || keyword.busy} />
          </Field>
          {upload.state.phase === 'error' ? <WriteError error={upload.state.error} /> : null}
          {keyword.state.phase === 'error' ? <WriteError error={keyword.state.error} /> : null}
          <FormActions>
            <Button
              type="button"
              variant="outline"
              disabled={keyword.busy || !reason.trim()}
              onClick={async () => (await keyword.run({ sensitive_keyword: true, reason: reason.trim() })) && onChanged()}
            >
              {t('hstm.sensitiveKeyword')}
            </Button>
            <Button type="submit" disabled={upload.busy || !file || !reason.trim()}>
              <Upload /> {tc('action.save')}
            </Button>
          </FormActions>
        </form>
      ) : null}
    </section>
  );
}
